import { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { Box } from '@chakra-ui/react';
import FormBase from '@/components/forms/new/FormBase';
import Alert from 'components/v4/Alert';
import AlertDescription from 'components/v4/AlertDescription';
import AlertIcon from 'components/v4/AlertIcon';
import { multiSelectDataMapper, toSentenceCase } from '@/utils/utils';
import updateServiceProfiles from '@/API/ServiceProfiles/updateServiceProfiles';
import createServiceProfile from '@/API/ServiceProfiles/createServiceProfile';
import ServiceProfileLoader from 'pages/services/serviceprofiles/ServiceProfileLoader';
import getACSRegions from '@/API/ServiceProfiles/getACSRegions';
import getACSProfile from '@/API/ServiceProfiles/getACSProfile';
// import ACSNameField from './formFields/ACSNameField';
import ACSVisibleScopeField from './formFields/ACSVisibleScopeField';
import ACSRegionsField from './formFields/ACSRegionsField';
import ACSMaxChannelField from './formFields/ACSMaxChannelField';
import BreadcrumbTrail from 'components/BreadcrumbTrail';
import useGetCurrentWholesaler from '@/queries/wholesaler/useGetCurrentWholesaler';
import { resolveWholesalerProfileName } from './serviceProfileName';

const ACS = ({ mode, data, level, handleBackButton, visibleScopeOptions }) => {
    const TEMPLATE_NAME = 'ACS Direct Routing';
    const MAX_CHANNEL_REQUIRED = 'Max Channel Count is required';
    const { requiredScope } = useSelector((state) => state.login);
    const { darkMode } = useSelector((state) => state.settings);
    const {
        currentWholesaler: wholesalerID,
        currentPartner: partnerID,
        companiesAndPartners,
    } = useSelector((state) => state.navigationLists);
    const { data: currentWholesalerData } = useGetCurrentWholesaler();

    const DEFAULT_ACS_PAYLOAD = {
        name: TEMPLATE_NAME,
        partnerID: level === 'partner' ? partnerID : undefined,
        wholesalerID: level === 'partner' ? undefined : wholesalerID,
        regions: [],
        visibleScope: level === 'partner' ? 20 : 60,
        maxChannel: '',
    };

    const [modalState, setModalState] = useState(data || DEFAULT_ACS_PAYLOAD);
    const [regionsEnum, setRegionsEnum] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [submitted, setSubmitted] = useState(false);

    useEffect(() => {
        if (
            mode !== 'EDIT' &&
            level !== 'partner' &&
            currentWholesalerData?.name
        ) {
            setModalState((prev) => ({
                ...prev,
                name: resolveWholesalerProfileName(
                    currentWholesalerData?.name,
                    TEMPLATE_NAME,
                ),
            }));
        } else if (
            mode !== 'EDIT' &&
            level === 'partner' &&
            companiesAndPartners?.length > 0
        ) {
            const partnerName = companiesAndPartners.find(
                ({ id }) => id === partnerID,
            )?.name;
            setModalState((prev) => ({
                ...prev,
                name: `${partnerName} - ${TEMPLATE_NAME} Partner Profile`,
            }));
        }
    }, [
        mode,
        companiesAndPartners,
        currentWholesalerData?.name,
        level,
        partnerID,
        wholesalerID,
        data?.wholesalerID,
        data?.partnerID,
    ]);

    // Helper to get scope string
    const scope = () => {
        if (level === 'partner') {
            return `Partner/${data?.partnerID || partnerID}`;
        } else {
            return `Wholesaler/${data?.wholesalerID || wholesalerID}`;
        }
    };

    useEffect(() => {
        setIsLoading(true);
        const fetchSPData = mode === 'EDIT' && data;
        Promise.all([
            fetchSPData
                ? getACSProfile({ level: scope(), scope: requiredScope })
                : new Promise((resolve) => setTimeout(resolve, 1)),
            getACSRegions({ scope: scope() }),
        ])
            .then(([spResult, regionsResult]) => {
                setRegionsEnum(regionsResult.data.map(multiSelectDataMapper));
                if (fetchSPData && spResult?.data) {
                    const regions =
                        spResult.data.regions?.map(multiSelectDataMapper) || [];
                    setModalState({ ...spResult.data, regions });
                }
            })
            .catch((e) =>
                setError(
                    e?.response?.data?.message ||
                        e?.message ||
                        'An error occurred loading service profile data.',
                ),
            )
            .finally(() => setIsLoading(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        mode,
        data,
        level,
        partnerID,
        currentWholesalerData?.name,
        companiesAndPartners,
        wholesalerID,
    ]);

    // Helper to map value(s) to option(s) for select fields
    const mapToOptions = (values, options) => {
        if (!values || !options) return [];
        if (Array.isArray(values)) {
            return (
                values
                    .map((v) => options.find((o) => o.value === (v.value ?? v)))
                    ?.filter(Boolean) || []
            );
        }
        return (
            options.find((o) => o.value === (values.value ?? values)) || null
        );
    };

    // Formik initial values (always use modalState, which is updated after async fetch)
    const initialValues = {
        name: modalState?.name || '',
        regions: mapToOptions(modalState?.regions, regionsEnum),
        maxChannel: modalState?.maxChannel || '',
        partnerID:
            level === 'partner'
                ? (modalState?.partnerID ?? partnerID)
                : undefined,
        wholesalerID:
            level === 'partner'
                ? undefined
                : (modalState?.wholesalerID ?? wholesalerID),
        visibleScope:
            modalState?.visibleScope ?? (level === 'partner' ? 20 : 60),
    };

    const validate = () => {
        const state = modalState || {};
        const errors = {};
        if (!state.name) errors.name = 'Name is required';
        if (!state.visibleScope)
            errors.visibleScope = 'Visible scope is required';
        if (!state.regions || state.regions.length === 0)
            errors.regions = 'At least one region is required';
        if (!state.maxChannel) errors.maxChannel = MAX_CHANNEL_REQUIRED;
        // setValidationErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleSubmit = () => {
        setError(null);
        setSubmitted(true);
        if (!validate()) {
            // A string, not an object — <AlertDescription> renders this directly
            // and an object would throw "Objects are not valid as a React child"
            setError(
                'There are some validation errors in the form. Please check and try again.',
            );
            return;
        }
        setIsLoading(true);
        const params = {
            type: 'ACSDirectRouting',
            payload: modalState,
            level,
            scope: requiredScope,
        };
        let result;
        if (mode === 'EDIT') {
            result = updateServiceProfiles(params);
        } else {
            result = createServiceProfile(params);
        }
        result
            .then(() => handleBackButton())
            .catch((e) =>
                setError(
                    e?.response?.data?.message ||
                        e?.message ||
                        'An error occurred. Please try again later.',
                ),
            )
            .finally(() => setIsLoading(false));
    };

    if (isLoading) return <ServiceProfileLoader />;
    const breadCrumbTrail = [
        { label: 'Services', path: ['', { index: 0 }] },
        { label: 'Configurations', path: '' },
    ];
    return (
        <>
            {/* {error && (
                <Box mb={4}>
                    <span style={{ color: '#E53E3E', fontSize: '1rem' }}>
                        {error?.response?.data?.message || 'An error occurred. Please try again later.'}
                    </span>
                </Box>
            )} */}
            <BreadcrumbTrail
                breadCrumbTrail={breadCrumbTrail}
                handleBackButton={handleBackButton}
            />
            <FormBase
                header={`${toSentenceCase(mode)} ${modalState.name || data?.name || ''}`}
                subTitleHeader={true}
                backLink={['/service-profiles', { state: { index: 0 } }]}
                ufBackBtn="builderBackBtn"
                ufSaveBtn="builderSaveBtn"
                customClick={true}
                initialValues={initialValues}
                onBack={handleBackButton}
                isLoading={isLoading}
                finalFormSubmit={handleSubmit}>
                {({ errors = {}, touched = {} }) =>
                    [
                        <ACSVisibleScopeField
                            key="visibleScope"
                            value={modalState.visibleScope}
                            onChange={(option) =>
                                setModalState((prev) => ({
                                    ...prev,
                                    visibleScope: option.value,
                                }))
                            }
                            options={visibleScopeOptions}
                            darkMode={darkMode}
                            minWidth="420px"
                            maxWidth="520px"
                            width="100%"
                            errors={errors}
                            touched={touched}
                        />,

                        <ACSRegionsField
                            key="regions"
                            value={modalState.regions}
                            onChange={(val) =>
                                setModalState((prev) => ({
                                    ...prev,
                                    regions: val,
                                }))
                            }
                            options={regionsEnum}
                            darkMode={darkMode}
                            minWidth="420px"
                            maxWidth="520px"
                            width="100%"
                            errors={errors}
                            touched={touched}
                            error={
                                !modalState.regions ||
                                modalState.regions.length === 0
                                    ? 'At least one region is required'
                                    : ''
                            }
                            showError={submitted}
                        />,

                        <ACSMaxChannelField
                            key="maxChannel"
                            value={modalState.maxChannel}
                            onChange={(e) =>
                                setModalState((prev) => ({
                                    ...prev,
                                    maxChannel: e.target.value,
                                }))
                            }
                            darkMode={darkMode}
                            minWidth="420px"
                            maxWidth="520px"
                            width="100%"
                            error={
                                !modalState.maxChannel
                                    ? MAX_CHANNEL_REQUIRED
                                    : ''
                            }
                            showError={submitted}
                        />,

                        error && (
                            <Box paddingBottom="0.5rem" key="error-box-acs">
                                <Alert
                                    key="error"
                                    status="error"
                                    mt={4}
                                    marginBottom="1rem">
                                    <AlertIcon />
                                    <AlertDescription>{error}</AlertDescription>
                                </Alert>
                            </Box>
                        ),
                    ].filter(Boolean)
                }
            </FormBase>
        </>
    );
};

// eslint-disable-next-line react-refresh/only-export-components
export default ACS;
